import {useEndOfPageScrollDebounce} from '@hconnect/common/hooks/useListScrollDebounce'
import {PaginationOptions} from '@hconnect/uikit'
import {Theme, useMediaQuery} from '@mui/material'
import React, {useEffect} from 'react'
import {useNavigate} from 'react-router'

import {
  combineSearchParamsToQueryString,
  useQueryParameterForViewMode,
  viewModeToUrlParams,
  useTasksPageQueryParams,
  useTasksPageFilters,
  UseTasksTableFiltersReturn
} from '../hooks'
import {
  ViewMode,
  TasksPageQueryParams,
  HProduceTask,
  PaginationResponse,
  UseTasksParams
} from '../types'
import {tasksPageQueryParamsToUrlParams} from '../utils'

import {TasksView} from './TasksView'

const getSearchParams = (nextViewMode: ViewMode, filtersParams: TasksPageQueryParams): string => {
  const viewModeParams = viewModeToUrlParams(nextViewMode)
  const tasksFiltersParams = tasksPageQueryParamsToUrlParams(filtersParams)
  return combineSearchParamsToQueryString(viewModeParams, tasksFiltersParams)
}

type Props = {
  timezone: string
  tasksTableFiltersOptions: UseTasksTableFiltersReturn
  paginationOptions: {
    pageNumber: number
    changePage: (pageNumber: number) => void
  }
  isLoading: boolean
  desktopData?: PaginationResponse<HProduceTask>
  mobileTasksData?: HProduceTask[]
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  itemsPerPage: number
  setItemsPerPage: (value: number) => void
  tasksQueryParams: UseTasksParams
}

const DEFAULT_PAGE_SIZE = 50

export const TasksContainer: React.FC<Props> = ({
  timezone,
  tasksTableFiltersOptions,
  paginationOptions: paginationSettings,
  isLoading,
  desktopData,
  mobileTasksData,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  itemsPerPage,
  setItemsPerPage,
  tasksQueryParams
}) => {
  const navigate = useNavigate()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const {pageNumber, changePage} = paginationSettings
  const [filtersParams] = useTasksPageQueryParams()

  const {areFiltersApplied} = useTasksPageFilters()

  const tasks = isMobile ? (mobileTasksData ?? []) : (desktopData?.items ?? [])
  const shouldFetchNextPage = hasNextPage && !isFetchingNextPage

  const {trackScrolling} = useEndOfPageScrollDebounce(() => {
    void fetchNextPage()
  }, shouldFetchNextPage)

  useEffect(() => {
    if (isMobile) {
      document.addEventListener('scroll', trackScrolling)
    }

    return () => document.removeEventListener('scroll', trackScrolling)
  }, [isMobile, trackScrolling])

  useEffect(() => {
    if (isMobile) {
      setItemsPerPage(DEFAULT_PAGE_SIZE)
      changePage(0)
    }
  }, [isMobile, changePage, setItemsPerPage])

  const onPaginationChange = (pageNumber: number, itemsPerPage: number) => {
    changePage(pageNumber)
    setItemsPerPage(itemsPerPage)
  }

  const paginationOptions: PaginationOptions = {
    page: pageNumber,
    rowsPerPage: itemsPerPage,
    onPaginationChange: onPaginationChange,
    totalDataLength: desktopData?.totalItemCount ?? 0
  }

  const viewMode = useQueryParameterForViewMode()
  const setViewMode = (nextViewMode: ViewMode) => {
    void navigate({search: getSearchParams(nextViewMode, filtersParams)})
  }

  return (
    <TasksView
      events={tasks}
      isLoading={isLoading}
      viewMode={viewMode}
      setViewMode={setViewMode}
      paginationOptions={isMobile ? undefined : paginationOptions}
      tableFiltersOptions={tasksTableFiltersOptions}
      timezone={timezone}
      showBottomProgress={isMobile && hasNextPage && isFetchingNextPage}
      areFiltersApplied={areFiltersApplied}
      statusesSummaryParams={tasksQueryParams}
    />
  )
}
